// src/pages/ProfilePage.js
import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { db } from "../firebase";
import { collection, query, where, getDocs, doc, setDoc, increment } from "firebase/firestore";

import "../styles/ProfilePage.css";
import "../styles/SocialIcons.css";

import {
  FaPhone,
  FaEnvelope,
  FaWhatsapp,
  FaInstagram,
  FaFacebook,
  FaTiktok,
  FaLinkedin,
  FaSnapchat,
  FaTwitter,
  FaGlobe,
} from "react-icons/fa";

import { QRCodeCanvas } from "qrcode.react";

/* ----------------------- Helpers ----------------------- */

const CONTACT_ORDER = ["phone", "email", "whatsapp", "instagram", "facebook", "tiktok", "linkedin", "snapchat", "x"];

const ICON_BY_TYPE = {
  phone: <FaPhone />,
  email: <FaEnvelope />,
  whatsapp: <FaWhatsapp />,
  instagram: <FaInstagram />,
  facebook: <FaFacebook />,
  tiktok: <FaTiktok />,
  linkedin: <FaLinkedin />,
  snapchat: <FaSnapchat />,
  x: <FaTwitter />,
  default: <FaGlobe />,
};

const getIconForLink = (linkType) => ICON_BY_TYPE[linkType] ?? ICON_BY_TYPE.default;

const labelFor = (key) => {
  if (key === "x") return "X (Twitter)";
  if (key.toLowerCase() === "whatsapp") return "WhatsApp";
  return key.charAt(0).toUpperCase() + key.slice(1);
};

const hexToRgba = (hex, alpha = 1) => {
  try {
    const h = String(hex || "#000").replace("#", "");
    const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
    const n = parseInt(full, 16);
    const r = (n >> 16) & 255;
    const g = (n >> 8) & 255;
    const b = n & 255;
    return `rgba(${r}, ${g}, ${b}, ${Math.min(Math.max(alpha, 0), 1)})`;
  } catch {
    return `rgba(0,0,0,${Math.min(Math.max(alpha, 0), 1)})`;
  }
};

/* ===================== NEW: URL Normalization ===================== */

const ensureProtocol = (url) => {
  const v = String(url || "").trim();
  if (!v) return "";

  // Already has supported protocol
  if (/^(https?:\/\/|mailto:|tel:|sms:)/i.test(v)) return v;

  // Starts with www -> https://
  if (/^www\./i.test(v)) return `https://${v}`;

  // Looks like a phone number -> tel:
  if (/^\+?\d[\d\s\-()]{6,}$/.test(v)) return `tel:${v.replace(/\s+/g, "")}`;

  // Otherwise assume it's a url missing protocol
  return `https://${v}`;
};

const normalizeContactUrlByType = (type, raw) => {
  const v = String(raw || "").trim();
  if (!v) return "";

  if (type === "phone") {
    // Supports: +972..., 0599..., tel:+...
    if (/^tel:/i.test(v)) return v;
    const cleaned = v.replace(/\s+/g, "");
    return `tel:${cleaned}`;
  }

  if (type === "email") {
    if (/^mailto:/i.test(v)) return v;
    return `mailto:${v}`;
  }

  if (type === "whatsapp") {
    // Supports:
    // - wa.me/XXXXXXXX
    // - https://wa.me/XXXXXXXX
    // - +972...
    if (v.includes("wa.me/")) return v.startsWith("http") ? v : `https://${v}`;
    const digits = v.replace(/[^0-9]/g, "");
    if (digits) return `https://wa.me/${digits}`;
    return "";
  }

  // Social / other: ensure https
  return ensureProtocol(v);
};

/* ===================== Buttons builders ===================== */

const convertContactToButtons = (contactsObj) => {
  if (!contactsObj) return [];
  const out = [];
  for (const key of CONTACT_ORDER) {
    const raw = contactsObj[key];
    const url = normalizeContactUrlByType(key, raw);
    if (url && String(url).trim() !== "") {
      out.push({
        id: `contact-${key}`,
        title: labelFor(key),
        url,
        type: key,
        isContact: true,
      });
    }
  }
  return out;
};

const normalizeCustomLinks = (customLinks = []) =>
  (Array.isArray(customLinks) ? customLinks : []).map((l) => {
    const rawUrl = l?.url || "";
    const safeUrl = ensureProtocol(rawUrl);
    return {
      id: l?.id || `custom-${safeUrl}-${Math.random().toString(36).slice(2, 8)}`,
      title: l?.title || rawUrl || "Link",
      url: safeUrl || "#",
      type: "custom",
      isContact: false,
    };
  });

const prettifyValue = (key, raw) => {
  if (!raw) return "";
  let v = String(raw);
  if (key === "phone") v = v.replace(/^tel:/i, "");
  if (key === "email") v = v.replace(/^mailto:/i, "");
  if (key === "whatsapp") {
    if (v.includes("wa.me/")) {
      v = `+${v.split("wa.me/")[1].replace(/[^0-9]/g, "")}`;
    }
  }
  return v;
};

const getThemeContainerStyle = (theme, themeSettings = {}) => {
  const force = themeSettings.forceBg || { mode: "inherit" };
  if (force.mode && force.mode !== "inherit") {
    const base = {
      fontFamily: force.fontFamily || "Arial, sans-serif",
      color: "#000",
      minHeight: "100vh",
      display: "flex",
      flexDirection: "column",
    };
    if (force.mode === "color") return { ...base, background: force.color || "#ffffff" };
    if (force.mode === "gradient") {
      const ang = force.gradientAngle ?? 180;
      return {
        ...base,
        background: `linear-gradient(${ang}deg, ${force.gradientStart || "#4a6f7b"}, ${force.gradientEnd || "#2f4e61"})`,
      };
    }
  }

  if (theme === "custom") {
    const {
      backgroundType = "color",
      backgroundColor = "#ffffff",
      gradientStart = "#4a6f7b",
      gradientEnd = "#2f4e61",
      gradientAngle = 180,
      fontFamily = "Arial, sans-serif",
    } = themeSettings;

    const base = {
      fontFamily,
      color: "#000",
      minHeight: "100vh",
      display: "flex",
      flexDirection: "column",
    };

    if (backgroundType === "gradient") {
      return { ...base, background: `linear-gradient(${gradientAngle}deg, ${gradientStart}, ${gradientEnd})` };
    }
    return { ...base, background: backgroundColor };
  }

  switch (theme) {
    case "dark":
      return { background: "#111", color: "#fff", minHeight: "100vh", display: "flex", flexDirection: "column", fontFamily: "Arial, sans-serif" };
    case "forest":
      return { background: "linear-gradient(160deg,#0f3d2e,#0a1f1a)", color: "#e9ffe9", minHeight: "100vh", display: "flex", flexDirection: "column" };
    case "neon":
      return { background: "radial-gradient(circle at 20% 20%, #1a002b 0%, #000 80%)", color: "#39ff14", minHeight: "100vh", display: "flex", flexDirection: "column" };
    case "sunset":
      return { background: "linear-gradient(180deg, #ff9a3c 0%, #ff2e63 50%, #120078 100%)", color: "#fff", minHeight: "100vh", display: "flex", flexDirection: "column" };
    case "light":
    default:
      return { background: "linear-gradient(180deg, #4a6f7b 0%, #31506a 60%, #ffffff 100%)", color: "#000", minHeight: "100vh", display: "flex", flexDirection: "column" };
  }
};

const getButtonStyleForTheme = (theme, themeSettings = {}) => {
  if (theme !== "custom") return {};
  const { buttonColor = "#ffffff", buttonFontColor = "#000000", buttonStyle = "rounded", fontFamily = "Arial, sans-serif" } = themeSettings;

  const radius = buttonStyle === "square" ? "6px" : buttonStyle === "rounded" ? "24px" : "20px";

  const common = {
    backgroundColor: buttonStyle === "outline" ? "transparent" : buttonColor,
    color: buttonFontColor,
    border: buttonStyle === "outline" ? `2px solid ${buttonColor}` : "2px solid transparent",
    borderRadius: radius,
    fontFamily,
    boxShadow: "0 18px 32px rgba(0,0,0,0.18), 0 2px 4px rgba(0,0,0,0.1)",
  };

  const inverted = {
    ...common,
    backgroundColor: buttonStyle === "outline" ? buttonColor : buttonFontColor,
    color: buttonStyle === "outline" ? "#fff" : buttonColor,
    borderColor: buttonStyle === "outline" ? buttonColor : "transparent",
  };

  return { common, inverted };
};

/* ---------- Reusable Save Contact button (موحّد لكل الأوضاع) ---------- */
const SaveContactButton = ({ onClick, theme, buttonStyles, small = false }) => (
  <button
    onClick={onClick}
    className="link-button vcard-button"
    style={{
      padding: small ? "10px 12px" : "14px 16px",
      borderRadius: small ? 10 : 14,
      fontSize: small ? 16 : 18,
      fontWeight: 800,
      border: "none",
      cursor: "pointer",
      alignSelf: "flex-start",
      ...(theme === "custom"
        ? buttonStyles?.inverted || {}
        : { background: "#fff", color: "#000", boxShadow: "0 6px 14px rgba(0,0,0,0.12)" }),
    }}
  >
    Save Contact
  </button>
);

/* ----------------------- Component ----------------------- */
function ProfilePage() {
  const { username } = useParams();
  const [profile, setProfile] = useState(null);
  const [profileId, setProfileId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProfile = async () => {
      setLoading(true);
      setError(null);
      try {
        const q = query(collection(db, "users"), where("username", "==", username));
        const querySnapshot = await getDocs(q);

        if (querySnapshot.empty) {
          setError("User not found!");
          setProfile(null);
        } else {
          const userDoc = querySnapshot.docs[0];
          setProfile(userDoc.data());
          setProfileId(userDoc.id);
          await setDoc(doc(db, "users", userDoc.id), { views: increment(1) }, { merge: true });
        }
      } catch (err) {
        console.error("Firebase Error:", err);
        setError("Error fetching profile.");
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [username]);

  const handleSaveContact = () => {
    if (!profile || !profile.contactLinks) return;

    const { contactLinks } = profile;

    // Normalize phone/email/whatsapp values for vCard
    const phoneHref = normalizeContactUrlByType("phone", contactLinks.phone); // tel:+...
    const emailHref = normalizeContactUrlByType("email", contactLinks.email); // mailto:...
    const waHref = normalizeContactUrlByType("whatsapp", contactLinks.whatsapp); // https://wa.me/...

    const phone = phoneHref ? phoneHref.replace(/^tel:/i, "") : "";
    const email = emailHref ? emailHref.replace(/^mailto:/i, "") : "";

    let whatsapp = "";
    if (waHref && waHref.includes("wa.me/")) {
      whatsapp = `+${waHref.split("wa.me/")[1].replace(/[^0-9]/g, "")}`;
    }

    const first = (profile.displayName || "").split(" ")[0] || "";
    const rest = (profile.displayName || "").split(" ").slice(1).join(" ") || "";

    const vCardData = `BEGIN:VCARD
VERSION:3.0
N:${rest};${first};;;
FN:${profile.displayName || ""}
${profile.profileType === "company" ? `ORG:${profile.displayName || ""}` : ""}
${phone ? `TEL;TYPE=CELL;TYPE=VOICE;TYPE=pref:${phone}` : ""}
${email ? `EMAIL:${email}` : ""}
${whatsapp ? `TEL;TYPE=WORK;TYPE=VOICE:${whatsapp}` : ""}
${profile.photoURL ? `PHOTO;VALUE=URL:${profile.photoURL}` : ""}
END:VCARD`;

    const blob = new Blob([vCardData], { type: "text/vcard;charset=utf-8" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${profile.username || "contact"}.vcf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) return <div style={{ textAlign: "center", marginTop: 50 }}>Loading Profile...</div>;
  if (error) return <div style={{ textAlign: "center", marginTop: 50, color: "red" }}>{error}</div>;
  if (!profile) return null;

  /* --------- Derived UI data --------- */
  const theme = profile.theme || "light";
  const ts = profile.themeSettings || {};
  const containerStyle = getThemeContainerStyle(theme, ts);
  const buttonStyles = getButtonStyleForTheme(theme, ts);

  const avatarSize = ts.avatarSize ?? 120;
  const avatarShape = ts.avatarShape || "circle";
  const avatarObjectFit = ts.avatarObjectFit || "cover";
  const avatarBorderWidth = ts.avatarBorderWidth ?? 4;
  const avatarBorderColor = ts.avatarBorderColor || "rgba(255,255,255,0.9)";
  const avatarBgMode = ts.avatarBgMode || "transparent";
  const avatarBgColor = ts.avatarBgColor || "#ffffff";
  const avatarBgOpacity = typeof ts.avatarBgOpacity === "number" ? ts.avatarBgOpacity : 1;

  const avatarRadius = avatarShape === "circle" ? "999px" : avatarShape === "rounded" ? "22px" : "0px";

  const avatarContainer = {
    width: avatarSize,
    height: avatarSize,
    borderRadius: avatarRadius,
    margin: "0 auto 16px auto",
    display: "grid",
    placeItems: "center",
    overflow: "hidden",
    background: avatarBgMode === "color" ? hexToRgba(avatarBgColor, avatarBgOpacity) : "transparent",
    border: `${avatarBorderWidth}px solid ${avatarBorderColor}`,
    boxShadow: "0 18px 36px rgba(0,0,0,0.26), 0 2px 4px rgba(0,0,0,0.18)",
  };

  const avatarImg = {
    width: "100%",
    height: "100%",
    objectFit: avatarObjectFit,
    display: "block",
  };

  const contactLinks = profile.contactLinks || {};
  const customLinks = normalizeCustomLinks(profile.customLinks);
  const contactButtons = convertContactToButtons(contactLinks);
  const mode = (profile.contactLinksDisplay || "icons").trim();

  const textColor = containerStyle.color || "#000";

  /* --------- Blocks --------- */

  const IconsBar = () =>
    contactButtons.length > 0 ? (
      <div
        className="social-icons-container"
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 12,
          justifyContent: "center",
          marginTop: 8,
        }}
      >
        {contactButtons.map((btn) => (
          <a
            key={btn.id}
            href={btn.url}
            target="_blank"
            rel="noopener noreferrer"
            className="social-icon"
            style={{
              width: 42,
              height: 42,
              borderRadius: 999,
              display: "grid",
              placeItems: "center",
              background: "rgba(255,255,255,0.2)",
              color: textColor,
              textDecoration: "none",
              backdropFilter: "blur(2px)",
            }}
            aria-label={btn.title}
            title={btn.title}
          >
            {getIconForLink(btn.type)}
          </a>
        ))}
      </div>
    ) : null;

  const BigButtons = ({ items }) => (
    <div
      className="links-list"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 14,
        padding: "0 18px 28px",
        maxWidth: 720,
        width: "100%",
        margin: "0 auto",
      }}
    >
      <button
        onClick={handleSaveContact}
        className="link-button vcard-button"
        style={{
          padding: "16px 18px",
          borderRadius: 18,
          fontSize: 20,
          fontWeight: 800,
          border: "none",
          cursor: "pointer",
          ...(theme === "custom"
            ? buttonStyles.inverted
            : { background: "#fff", color: "#000", boxShadow: "0 18px 32px rgba(0,0,0,0.18), 0 2px 4px rgba(0,0,0,0.1)" }),
        }}
      >
        Save Contact
      </button>

      {items.map((link) => (
        <a
          key={link.id}
          href={link.url}
          target="_blank"
          rel="noopener noreferrer"
          className="link-button"
          style={{
            display: "block",
            width: "100%",
            textAlign: "center",
            padding: "18px 16px",
            fontSize: 24,
            fontWeight: 800,
            textDecoration: "none",
            borderRadius: 18,
            color: "#1a1a1a",
            background: "rgba(255,255,255,0.85)",
            boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.45), 0 18px 32px rgba(0,0,0,0.18), 0 2px 4px rgba(0,0,0,0.1)",
            ...(theme === "custom" ? buttonStyles.common : {}),
          }}
        >
          {link.title}
        </a>
      ))}
    </div>
  );

  const Chips = ({ items }) => {
    if (items.length === 0) return null;
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 12,
          padding: "0 18px 28px",
          maxWidth: 820,
          margin: "0 auto",
        }}
      >
        <SaveContactButton onClick={handleSaveContact} theme={theme} buttonStyles={buttonStyles} small />

        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, justifyContent: "center" }}>
          {items.map((link) => (
            <a
              key={link.id}
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                padding: "10px 14px",
                borderRadius: 999,
                textDecoration: "none",
                fontWeight: 700,
                fontSize: 15,
                background: "rgba(255,255,255,0.85)",
                color: "#1a1a1a",
                boxShadow: "0 6px 14px rgba(0,0,0,0.15)",
                ...(theme === "custom" ? buttonStyles.common : {}),
              }}
            >
              {link.isContact ? <span style={{ marginRight: 8, verticalAlign: -2 }}>{getIconForLink(link.type)}</span> : null}
              {link.title}
            </a>
          ))}
        </div>
      </div>
    );
  };

  const ListMode = ({ items }) => {
    if (items.length === 0) return null;
    return (
      <div
        style={{
          padding: "0 18px 28px",
          maxWidth: 720,
          width: "100%",
          margin: "0 auto",
          display: "flex",
          flexDirection: "column",
          gap: 10,
        }}
      >
        <SaveContactButton onClick={handleSaveContact} theme={theme} buttonStyles={buttonStyles} />

        {items.map((it) => (
          <a
            key={it.id}
            href={it.url}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              background: "rgba(255,255,255,0.9)",
              borderRadius: 12,
              padding: "12px 14px",
              textDecoration: "none",
              color: "#1a1a1a",
              boxShadow: "0 10px 20px rgba(0,0,0,0.12)",
              ...(theme === "custom" ? buttonStyles.common : {}),
            }}
          >
            {it.isContact ? <span style={{ fontSize: 18 }}>{getIconForLink(it.type)}</span> : null}
            <span style={{ fontWeight: 800 }}>{it.title}</span>

            <span style={{ marginLeft: "auto", opacity: 0.8, fontFamily: "monospace" }}>
              {it.isContact ? prettifyValue(it.type, it.url) : it.url}
            </span>
          </a>
        ))}
      </div>
    );
  };

  const Grid = ({ items }) => {
    if (items.length === 0) return null;
    return (
      <div
        style={{
          padding: "0 18px 28px",
          maxWidth: 900,
          width: "100%",
          margin: "0 auto",
          display: "flex",
          flexDirection: "column",
          gap: 14,
        }}
      >
        <SaveContactButton onClick={handleSaveContact} theme={theme} buttonStyles={buttonStyles} />

        <div style={{ display: "grid", gap: 14, gridTemplateColumns: "repeat(2, minmax(0, 1fr))" }}>
          {items.map((card) => (
            <a
              key={card.id}
              href={card.url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 10,
                background: "rgba(255,255,255,0.9)",
                borderRadius: 16,
                padding: "16px 14px",
                textDecoration: "none",
                color: "#1a1a1a",
                boxShadow: "0 14px 26px rgba(0,0,0,0.14)",
                ...(theme === "custom" ? buttonStyles.common : {}),
              }}
            >
              {card.isContact ? <div style={{ fontSize: 22 }}>{getIconForLink(card.type)}</div> : null}
              <div style={{ fontWeight: 800, fontSize: 20, textAlign: "center" }}>{card.title}</div>
              {!card.isContact ? (
                <div style={{ fontSize: 13, opacity: 0.7, textAlign: "center", wordBreak: "break-all" }}>{card.url}</div>
              ) : null}
            </a>
          ))}
        </div>
      </div>
    );
  };

  const MinimalText = ({ items }) => {
    if (items.length === 0) return null;
    return (
      <div
        style={{
          padding: "0 18px 28px",
          maxWidth: 820,
          width: "100%",
          margin: "0 auto",
          display: "flex",
          flexDirection: "column",
          gap: 8,
        }}
      >
        <SaveContactButton onClick={handleSaveContact} theme={theme} buttonStyles={buttonStyles} small />

        {items.map((it) => (
          <a
            key={it.id}
            href={it.url}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              textDecoration: "none",
              fontWeight: 700,
              fontSize: 18,
              color: textColor,
              opacity: 0.95,
            }}
          >
            {it.isContact ? <span style={{ marginRight: 8, verticalAlign: -2 }}>{getIconForLink(it.type)}</span> : null}
            {it.title}
          </a>
        ))}
      </div>
    );
  };

  const QrBlock = ({ items }) => {
    const pageUrl = `${window.location.origin}/${profile.username || username}`;
    return (
      <div
        style={{
          padding: "0 18px 28px",
          maxWidth: 900,
          width: "100%",
          margin: "0 auto",
          display: "grid",
          gap: 18,
          gridTemplateColumns: "1fr",
        }}
      >
        <div
          style={{
            background: "rgba(255,255,255,0.9)",
            borderRadius: 16,
            padding: 18,
            boxShadow: "0 14px 26px rgba(0,0,0,0.14)",
            textAlign: "center",
            ...(theme === "custom" ? buttonStyles.common : {}),
          }}
        >
          <h3 style={{ marginTop: 0, marginBottom: 10 }}>Scan My QR</h3>
          <QRCodeCanvas id="profile-qr" value={pageUrl} size={220} bgColor="#ffffff" fgColor="#000000" level="H" includeMargin />
          <p style={{ marginTop: 10, opacity: 0.8, color: "#1a1a1a" }}>{pageUrl}</p>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <SaveContactButton onClick={handleSaveContact} theme={theme} buttonStyles={buttonStyles} />

          {items.map((link) => (
            <a
              key={link.id}
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              className="link-button"
              style={{
                display: "block",
                width: "100%",
                textAlign: "center",
                padding: "16px 14px",
                fontSize: 20,
                fontWeight: 800,
                textDecoration: "none",
                borderRadius: 16,
                color: "#1a1a1a",
                background: "rgba(255,255,255,0.85)",
                boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.45), 0 18px 32px rgba(0,0,0,0.18), 0 2px 4px rgba(0,0,0,0.1)",
                ...(theme === "custom" ? buttonStyles.common : {}),
              }}
            >
              {link.title}
            </a>
          ))}
        </div>
      </div>
    );
  };

  /* ---------- Main Render ---------- */
  const headerSubtitle = (
    <div style={{ marginTop: 6 }}>
      <div style={{ opacity: 0.9, color: textColor, fontStyle: "italic" }}>
        {profile.profileType === "company" ? "Company Account" : "Personal Account"}
      </div>
      <div style={{ opacity: 0.9, color: textColor, marginTop: 2 }}>@{profile.username}</div>
    </div>
  );

  // عناصر العرض حسب الوضع المختار
  const itemsForButtons = [...customLinks, ...contactButtons];
  const itemsForIcons = customLinks; // فقط custom كأزرار، وروابط التواصل كأيقونات بالأعلى

  return (
    <div className="profile-container" style={{ ...containerStyle, alignItems: "stretch" }}>
      {/* Header */}
      <div className="profile-header" style={{ padding: "36px 18px 18px", textAlign: "center" }}>
        {profile.photoURL && (
          <div style={avatarContainer}>
            <img src={profile.photoURL} alt={profile.displayName || "Profile"} style={avatarImg} />
          </div>
        )}

        <h1 style={{ margin: 0, fontSize: 34, lineHeight: 1.18, fontWeight: 800, color: textColor }}>
          {profile.displayName || profile.username}
        </h1>

        {headerSubtitle}

        {mode === "icons" && <IconsBar />}
      </div>

      {/* Modes */}
      {mode === "icons" && <BigButtons items={itemsForIcons} />}
      {mode === "buttons" && <BigButtons items={itemsForButtons} />}
      {mode === "chips" && <Chips items={itemsForButtons} />}
      {mode === "list" && <ListMode items={itemsForButtons} />}
      {mode === "grid" && <Grid items={itemsForButtons} />}
      {mode === "minimal_text" && <MinimalText items={itemsForButtons} />}
      {mode === "qr_block" && <QrBlock items={itemsForButtons} />}

      {/* Watermark */}
      {profile.plan === "free" && (
        <p
          style={{
            marginTop: "auto",
            textAlign: "center",
            padding: "12px 0 18px",
            color: "rgba(255,255,255,0.7)",
            fontSize: "0.9em",
          }}
        >
          Powered by GrowUp Tech
        </p>
      )}
    </div>
  );
}

export default ProfilePage;
